import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, Users, TrendingUp, Clock, ChevronRight,
  Star, CreditCard, Loader2, AlertCircle, CheckCircle2, XCircle, Timer, Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  confirmed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Timer },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  completed: { bg: 'bg-slate-100', text: 'text-slate-700', icon: CheckCircle2 }
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/bookings/summary`, { headers })
        ]);
        setStats(statsRes.data);
        setSummary(summaryRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  const BookingSummaryCard = ({ title, data, icon: Icon, color, onClick }) => {
    const counts = data?.counts || { total: 0, confirmed: 0, pending: 0, cancelled: 0 };
    return (
      <div
        className="bg-white rounded-xl p-5 border border-[#E7E5E4] hover:shadow-md transition-shadow cursor-pointer"
        onClick={onClick}
        data-testid={`summary-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <span className="text-3xl font-bold text-[#1C1917]">{counts.total}</span>
        </div>
        <p className="text-sm font-medium text-[#57534E] mb-2">{title}</p>
        <div className="flex gap-3 text-xs">
          {counts.confirmed > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />{counts.confirmed}
            </span>
          )}
          {counts.pending > 0 && (
            <span className="flex items-center gap-1 text-yellow-600">
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />{counts.pending}
            </span>
          )}
          {counts.cancelled > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />{counts.cancelled}
            </span>
          )}
        </div>
      </div>
    );
  };

  const todayBookings = summary?.today?.bookings || [];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#1C1917]">Dashboard</h1>
        <p className="text-[#57534E]">Centro operativo de Madrid Tuk Tuk Tour</p>
      </div>

      {/* Booking Summary Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-[#1C1917]">Reservas</h2>
          <Button variant="ghost" size="sm" className="text-[#C2410C]" onClick={() => navigate('/admin/calendar')}>
            Ver calendario <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <BookingSummaryCard
            title="Hoy"
            data={summary?.today}
            icon={CalendarDays}
            color="bg-[#C2410C]"
            onClick={() => navigate('/admin/calendar')}
          />
          <BookingSummaryCard
            title="Manana"
            data={summary?.tomorrow}
            icon={Clock}
            color="bg-blue-500"
            onClick={() => navigate('/admin/calendar')}
          />
          <BookingSummaryCard
            title="Esta semana"
            data={summary?.week}
            icon={TrendingUp}
            color="bg-purple-500"
            onClick={() => navigate('/admin/calendar')}
          />
          <BookingSummaryCard
            title="Proximo mes"
            data={summary?.month}
            icon={Users}
            color="bg-emerald-500"
            onClick={() => navigate('/admin/calendar')}
          />
        </div>
      </div>

      {/* Today's Bookings Detail */}
      {todayBookings.length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]">
          <h3 className="font-display font-semibold text-[#1C1917] mb-4">Reservas de hoy</h3>
          <div className="space-y-2">
            {todayBookings.map((b) => {
              const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
              const StatusIcon = sc.icon;
              return (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg bg-[#FAFAF9] hover:bg-[#F5F5F4] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full ${sc.bg} flex items-center justify-center`}>
                      <StatusIcon className={`h-4 w-4 ${sc.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[#1C1917]">{b.name}</p>
                      <p className="text-xs text-[#78716C]">{b.tour_name || b.tour_id} - {b.adults} pax</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm text-[#1C1917]">{b.time}</p>
                    <p className="text-xs text-[#C2410C]">{b.total_price}EUR</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* General Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="stat-revenue">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="h-5 w-5 text-[#C2410C]" />
            <span className="text-sm text-[#57534E]">Ingresos totales</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{stats?.revenue?.total || 0}€</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="stat-tips">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-5 w-5 text-rose-500" />
            <span className="text-sm text-[#57534E]">Propinas</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{stats?.tips?.total || 0}€</p>
          <p className="text-xs text-rose-500">{stats?.tips?.count || 0} recibidas</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="stat-bookings">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-5 w-5 text-blue-500" />
            <span className="text-sm text-[#57534E]">Total reservas</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{stats?.bookings?.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="stat-reviews">
          <div className="flex items-center gap-3 mb-2">
            <Star className="h-5 w-5 text-[#F59E0B]" />
            <span className="text-sm text-[#57534E]">Valoraciones</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{stats?.reviews?.total || 0}</p>
          <p className="text-xs text-yellow-600">{stats?.reviews?.pending || 0} pendientes</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-[#E7E5E4]" data-testid="stat-discounts">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-purple-500" />
            <span className="text-sm text-[#57534E]">Codigos descuento</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{stats?.discounts?.total || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
